/**
 * funnelShared — types, tokens and helpers used by the Funnel Manager
 * (Marketing Emails) and by Free Tier Members (Members & Conversion).
 *
 * These two screens were one 1,000-line component until 2026-08-19. They still
 * share the activation-pipeline shape, so the pieces both need live here.
 */

import { EmailFunnelRow, EmailFunnelStepRow } from '../../../../services/api';

// ── Brand tokens (match the Activation Funnel page) ──────────────────────────
export const PHASE_COLOR: Record<string, string> = {
  '1': '#7D1935', '2': '#D4AC0D', '3': '#1A5F7A', '4': '#146152', '5': '#5D6D7E',
};
export const INK = '#1A1A1A';
export const SLATE = '#5D6D7E';
export const LINE = '#E7DECB';

export const TIER_COLOR: Record<string, { bg: string; fg: string }> = {
  hot: { bg: '#F1E3E7', fg: '#7D1935' },
  warm: { bg: '#FBF3DA', fg: '#9A7B0A' },
  cold: { bg: '#EAEEF1', fg: '#5D6D7E' },
};

// Funnel shapes are owned by the API layer (single source of truth).
export type FunnelStep = EmailFunnelStepRow;
export type Funnel = EmailFunnelRow;

export interface Phase {
  phase: string;
  phase_name: string;
  milestones: string;
  step_labels?: string[];
  funnel_key: string;
  color: string;
  idle_threshold_hours: number;
  member_count: number;
  pct_of_base: number;
  idle_flag_count: number;
  funnel: { slug: string; name: string; status: string; attached_promo_code: string | null } | null;
}

export interface MemberPos {
  user_id: string;
  name: string | null;
  email: string | null;
  current_phase: number;
  current_milestone: number;
  milestone_label: string;
  hours_idle: number | null;
  eligible_funnel_key: string;
  eligible_funnel_phase: string;
  is_subscriber: boolean;
  conversion_score: number | null;
  engagement_tier: string | null;
  enrolled_in_funnel: boolean;
  location_country_code?: string | null;
  location_region?: string | null;
  location_city?: string | null;
  location_source?: any;
  location_confidence?: any;
  location_resolved_at?: string | null;
}

export interface EligibleMemberRow {
  user_id: string;
  name: string | null;
  email: string | null;
  hours_idle: number | null;
  engagement_tier: string | null;
  is_subscriber: boolean | null;
}

// ── Naming ───────────────────────────────────────────────────────────────────
/**
 * Funnel display names are prefixed with their marketing number: "F4 — Convert
 * to Paid", "P1 — Habit Building", "F6b — Dormant Win-Back".
 *
 * Two traps this handles:
 *  - Migrations have written all three dash characters. The old regex matched
 *    only the em dash, so hyphenated names printed their prefix twice
 *    ("F3" badge next to a title reading "F4 - Convert to Paid").
 *  - The badge must come from the NAME, not from phase_key. Migration 1088 moved
 *    F4 onto phase 3, so a phase-derived badge disagreed with the title and with
 *    every agency document. The F-numbers are the marketing vocabulary; the
 *    phase is an implementation detail.
 */
export const FUNNEL_PREFIX_RE = /^([FP]\d+[a-z]?)\s*[—–-]\s*/;

export const funnelBadge = (f: Pick<Funnel, 'name' | 'phase_key'>): string => {
  const m = f.name.match(FUNNEL_PREFIX_RE);
  if (m) return m[1];
  return f.phase_key ? `F${f.phase_key}` : 'F·';
};

export const funnelDisplayName = (name: string): string =>
  name.replace(FUNNEL_PREFIX_RE, '');

/** Phase 5 covers activation phases 5 and 6, so it reads "5–6" everywhere. */
export const phaseLabel = (phase?: string | null): string =>
  phase === '5' ? '5–6' : String(phase ?? '');

// ── Grouping and ordering ────────────────────────────────────────────────────
/** PRE-SUBSCRIBER (conversion) vs POST-SUBSCRIBER (retention). */
export const funnelGroup = (f: Funnel): 'pre' | 'post' =>
  f.funnel_group === 'post' || (typeof f.slug === 'string' && f.slug.startsWith('post_'))
    ? 'post'
    : 'pre';

/**
 * Offer policy. Slug-based on purpose: this used to test `phase_key >= 4`, and
 * when migration 1088 moved F4 (Convert to Paid) onto phase 3 the Attach-offer
 * control silently vanished from the one funnel that exists to sell.
 * F1–F3 and F6 (dormant) are offer-free by design; the P-series too.
 */
const OFFER_CAPABLE_SLUGS = new Set([
  'three_plus_to_paid',        // F4 — Convert to Paid
  'f5_subscriber_onboarding',  // F5 — Subscriber Onboarding
  'account_deletion_winback',  // F7 — Account-Deletion Win-Back
]);
export const isOfferCapable = (f: Funnel): boolean => OFFER_CAPABLE_SLUGS.has(f.slug);

/** Sort key: F1, F2, F3, F4, F5, F6, F6b, F7 … then P1, P2, P3, P4. */
const badgeSortKey = (badge: string): number => {
  const m = badge.match(/^([FP])(\d+)([a-z]?)$/);
  if (!m) return 9999;
  const [, letter, num, suffix] = m;
  const base = (letter === 'P' ? 1000 : 0) + parseInt(num, 10) * 10;
  return base + (suffix ? suffix.charCodeAt(0) - 96 : 0); // F6 < F6b
};

/**
 * Order every funnel PRE-then-POST by its marketing number.
 *
 * Replaces a hand-picked list (phase funnels + two hardcoded slugs) that simply
 * dropped any funnel it did not name — which is why F3 (Keep the Story Going)
 * and F6b (Dormant Win-Back) were invisible on this screen despite being active
 * and enrolling Members.
 */
export const orderFunnels = (flows: Funnel[]): Funnel[] => {
  const rank = (f: Funnel) =>
    (funnelGroup(f) === 'post' ? 100000 : 0) + badgeSortKey(funnelBadge(f));
  return [...flows].sort((a, b) => rank(a) - rank(b) || a.slug.localeCompare(b.slug));
};

// ── Routes ───────────────────────────────────────────────────────────────────
export const FUNNEL_MANAGER_PATH = '/dashboard/communications/marketing/funnels';
export const FREE_TIER_MEMBERS_PATH = '/dashboard/free-tier';

export const templatePerformancePath = (templateKey: string): string =>
  `/dashboard/communications/marketing/templates/${encodeURIComponent(templateKey)}/performance`;

export const audienceDetailPath = (audienceId: string): string =>
  `/dashboard/communications/marketing/audiences/${audienceId}`;
